import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  PageHeader,
  Space,
  Card,
  Alert,
  Typography,
  Switch,
  Select,
} from 'antd';
import { KeyOutlined } from '@ant-design/icons';
import {
  handleApiSuccess,
  handleApiError,
  MyBreadcrumb,
  accessControls,
} from 'utils';
import { apiKeysUpdateService, apiKeysGetByIdService } from 'services/apikeys';
import { usersListService } from 'services/user';

const { Text } = Typography;
const { TextArea } = Input;

export default function UpdateApiKey() {
  const [form] = Form.useForm();
  const history = useHistory();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [apiKeyData, setApiKeyData] = useState(null);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    loadPermissions();
    fetchApiKeyData();
    loadUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPermissions = () => {
    accessControls.fetch().then((permissions) => {
      setPermissions(permissions);
      // Redirect if user doesn't have permission
      if (!accessControls.can(permissions, 'Client.update:any')) {
        history.push('/apikeys');
      }
    });
  };

  const loadUsers = () => {
    usersListService()
      .then((response) => {
        const usersList = response.data.items.map((item) => ({
          label: item.display_name,
          value: item._id,
        }));
        setUsers(usersList);
      })
      .catch((error) => {
        handleApiError(error);
      });
  };

  const fetchApiKeyData = async () => {
    try {
      // Fetch the API key data from the server
      const response = await apiKeysGetByIdService(id);
      const keyData = response.data ? response.data.items[0] : null;

      console.log(keyData);

      setApiKeyData(keyData);

      // Set form values with the fetched data
      form.setFieldsValue({
        key_name: keyData.key_name,
        description: keyData.description,
        is_active: keyData.is_active,
        user: keyData?.user?._id,
      });
    } catch (error) {
      handleApiError(error);
    }
  };

  const onFinish = (values) => {
    setIsLoading(true);
    apiKeysUpdateService(id, values)
      .then((response) => {
        setIsLoading(false);
        handleApiSuccess(response);
        history.push('/apikeys');
      })
      .catch((error) => {
        setIsLoading(false);
        handleApiError(error);
      });
  };

  return (
    <div>
      <MyBreadcrumb
        items={[
          { label: 'API Keys', path: '/apikeys' },
          { label: 'Update API Key', path: '' },
        ]}
      />
      <PageHeader
        ghost={false}
        onBack={() => history.push('/apikeys')}
        title="Update API Key"
        subTitle="Modify API key settings"
        avatar={{ icon: <KeyOutlined /> }}
      >
        <Card>
          <Form
            form={form}
            name="update_api_key"
            layout="vertical"
            onFinish={onFinish}
            initialValues={{ is_active: true }}
          >
            <Form.Item
              name="key_name"
              label="Key Name"
              rules={[
                {
                  required: true,
                  message: 'Key name is required',
                },
                {
                  max: 50,
                  message: 'Key name cannot exceed 50 characters',
                },
              ]}
            >
              <Input placeholder="Enter a name for this API key" />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
              rules={[
                {
                  required: true,
                  message: 'Description is required',
                },
                {
                  max: 100,
                  message: 'Description cannot exceed 100 characters',
                },
              ]}
            >
              <TextArea placeholder="Enter a description for this API key" />
            </Form.Item>

            <Form.Item
              name="user"
              label="User"
              rules={[
                {
                  required: true,
                  message: 'Please select a user',
                },
              ]}
              tooltip="This API key will follow the access control of the selected user. To modify permissions, update the user's role and access control settings."
            >
              <Select
                placeholder="Select a user"
                style={{ width: '75%' }}
                options={users}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>

            <Form.Item name="is_active" valuePropName="checked" label="Status">
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={isLoading}>
                  Update API Key
                </Button>
                <Button onClick={() => history.push('/apikeys')}>Cancel</Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </PageHeader>
    </div>
  );
}
